import { 
    ShareBaseItem,
    PermissionType,
    pxItemTypeGuards
} from 'phoenix/domain';
import {
    ShareBaseDialog,
    ShareBaseItemService, 
    IAppAction
} from 'sharebase/domain';
import { DeferredPermissionMenuItem } from 'sharebase-app/domain/deferredPermissionMenuItem';
import { ActionRepository } from 'sharebase/services/actionRepository';
import { IDocumentService } from 'sharebase/services/documentService';
import { IFolderService } from 'sharebase/services/folderService';
import { LibraryService } from 'sharebase/services/libraryService';
import { ToastService } from 'sharebase/services/toastService';
import { AclDatastore } from 'sharebase/dataStores/aclDatastore';
import { AppAction } from 'sharebase-app/components/_app/app.actions';
import { TrackingService } from 'sharebase/services/trackingService/'
 
class SelectableMenuItem {
    public readonly:boolean = false;

    private _selected:boolean = false;
    public get selected():boolean {
        return this._selected; 
    }
    public set selected(val) {
        if(!this.readonly){
            this._selected = val;
        }
    }

    public get icon():string {
        return this.source.icon;
    };
    public get label():string {
        return this.source.label
    }
    public get action():string{
        return this.source.action;
    }

    constructor(
        private source:IAppAction,
        
    ){        
    }
}

class PermissionSelectTile extends SelectableMenuItem{
    constructor(
        action:IAppAction, 
        public perm:PermissionType = PermissionType.FULLCONTROL,
        
    ){
        super(action);
    }
}

const template = require('./requestPermissionsDialog.template');
require('./requestPermissionsDialog.styles');

const selectableActions = Object.freeze({
    general: [
        AppAction.Copy,
        AppAction.Delete,
        AppAction.Download,
        AppAction.Move,
        AppAction.Favorite,
        AppAction.EditInShareBaseDesktop,
        AppAction.Replace,
        AppAction.Rename
    ],
    document: [
        AppAction.CreateDocumentLinkShare,
        AppAction.CreateDocumentUserShare
    ],
    folder: [
        AppAction.CreateFolderLinkShare,
        AppAction.CreateFolderUserShare
    ]
});

export class RequestPermissionsDialog extends ShareBaseDialog {
    public static $inject = [
        '$mdDialog',
        '$mdMedia',
        '$q',
        'sbAclDatastore',
        'sbActionRepository',
        'sbDocumentService',
        'sbFolderService',
        'sbLibraryService',		
        'sbToastService',
        'gettextCatalog'
    ];

    private items:ShareBaseItem[];
    private requestedPermissions:PermissionType;
    private selectableTiles:PermissionSelectTile[] = [];
    private targetPermission:PermissionSelectTile;

    protected get actionSelected():boolean {
        return Object.keys(this.selectableTiles)
            .map(k => this.selectableTiles[k])
            .some(a => a.selected);
    }

    constructor(
        $mdDialog,
        $mdMedia,
        private $q:ng.IQService,
        private sbAclDatastore:AclDatastore,
        private sbActionRepository:ActionRepository,
        private sbDocumentService:IDocumentService,
        private sbFolderService:IFolderService,
        private sbLibraryService:LibraryService,
        private sbToastService:ToastService,
        protected gettextCatalog:ng.gettext.gettextCatalog
    ){
        super($mdDialog, template, gettextCatalog.getString('Request additional permissions?'), $mdMedia('xs'));
    }

    public open(e:ng.IAngularEvent, items:ShareBaseItem|ShareBaseItem[], requestedPerms:PermissionType, targetAction:IAppAction, requestableActions?:IAppAction[]){
        this.items = Array.isArray(items) ? items : [items];
        this.requestedPermissions = requestedPerms;
        this.targetPermission = new PermissionSelectTile(targetAction, targetAction.permissions);        
        this.targetPermission.selected = true;
        this.targetPermission.readonly = true;        
        let p = this.$q.resolve();

        // for the MVP, only present a full list of requestable actions for a single action;
        // otherwise, just show the requested permission for all actions
        if(this.items.length === 1){
            let item =  this.items[0];
            p = this.sbAclDatastore.load(item).then(acl => {
                if(!requestableActions && !(item as any).DateDeleted) {
                    let requestableActionNames = selectableActions.general;
                    if(pxItemTypeGuards.isShareBaseDocument(item)){
                        requestableActionNames = requestableActionNames.concat(selectableActions.document);
                    }
                    else if(pxItemTypeGuards.isShareBaseFolder(item)){
                        requestableActionNames = requestableActionNames.concat(selectableActions.folder);
                    }
                
                    requestableActions = this.sbActionRepository.getActions(requestableActionNames);
                }
                else {
                    requestableActions = [];
                }
    
                this.selectableTiles = requestableActions
                    .filter(a => { 
                        // don't list actions the user already has rights to or is requesting
                        return angular.isDefined(a.permissions) &&
                            a.permissions !== PermissionType.NONE && 
                            a.action !== targetAction.action &&
                            !acl.hasPermission(a.permissions);
                    })
                    .map(a => new PermissionSelectTile(a, a.permissions));   
            });  
        }        

        p.then(() => {
            this.selectableTiles.push(this.targetPermission);
        })

        return super.open(e);
    } 

    public dismiss(){
        this.selectableTiles = [];
        this.items = [];
        this.requestedPermissions = this.targetPermission = null;
        super.dismiss();
    }

    public requestPermissions(){
        let successMsg = this.gettextCatalog.getString('Rights succesfully requested.');
        let failureMsg = this.gettextCatalog.getString('Unable to request rights.');

        if(this.items.length === 1) {            
            let { name } = this.getSpecifics(this.items[0]);
            successMsg = this.gettextCatalog.getString('Rights requested for "{{itemName}}".', {
                itemName: name
            });
            failureMsg = this.gettextCatalog.getString('Unable to request rights for "{{itemName}}".', {
                itemName: name
            });
        }

        let promises  = this.items.map(item => {
            let { service } = this.getSpecifics(item);

            if(!service){
                return this.$q.reject(new Error('No service available to request rights.'));
            }

            return service.requestPermissions(item, this.requestedPermissions)
        });

        return this.$q.all(promises)
            .then(() => {
                this.sbToastService.showSimple(successMsg);
            })
            .catch(() => {
                this.sbToastService.showSimple(failureMsg);
            })
            .finally(() => {
                this.dismiss();
            });
    }

    private getSpecifics(item:ShareBaseItem) {
        let service:ShareBaseItemService;
        let name = '';

        if(pxItemTypeGuards.isShareBaseDocument(item)){
            name = item.DocumentName;
            service = this.sbDocumentService;
        }
        else if(pxItemTypeGuards.isShareBaseFolder(item)){
            name = item.FolderName;
            service = this.sbFolderService;
        }
        else if (pxItemTypeGuards.isShareBaseLibrary(item)){
            name = item.LibraryName;
            service = this.sbLibraryService;
        }

        return {
            name,
            service
        }
    }
}

export class TrackableRequestPermissionsDialog extends RequestPermissionsDialog {
    public static $inject = [
        '$q',
        '$mdDialog',
        '$mdMedia',
        'sbAclDatastore',
        'sbActionRepository',
        'sbDocumentService',
        'sbFolderService',
        'sbLibraryService',	
        'sbToastService',
        'sbTrackingService',	
        'gettextCatalog'
    ];

    constructor(
        $q:ng.IQService,
        $mdDialog,
        $mdMedia,
        sbAclDatastore:AclDatastore,
        sbActionRepository:ActionRepository,
        sbDocumentService:IDocumentService,
        sbFolderService:IFolderService,
        sbLibraryService:LibraryService,
        sbToastService,
        private sbTrackingService:TrackingService,
        gettextCatalog
    ){
        super($mdDialog, $mdMedia, $q, sbAclDatastore, sbActionRepository, sbDocumentService, sbFolderService, sbLibraryService, sbToastService, gettextCatalog);
    }

    public open(e:ng.IAngularEvent, item:ShareBaseItem, requestedPerms:PermissionType, targetAction:DeferredPermissionMenuItem,){
        this.sbTrackingService.trackClick('Rights request started.')
        return super.open(e, item, requestedPerms, targetAction);
    }

    public requestPermissions(){
        return super.requestPermissions().then(() => {
            this.sbTrackingService.trackClick('Rights request completed.');
        });
    }
}


// WEBPACK FOOTER //
// ./src/sharebase-app/components/requestPermissionsDialog/requestPermissionsDialog.ts